import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import {
  ContractRefundPaymentItemAction,
  useContractRefundPaymentItemProvider,
} from '../Finance.ContractRefundPaymentItem.Hooks';
import ContractRefundPaymentItemActions from './Finance.ContractRefundPaymentItem.Actions';
import ContractRefundPaymentItemFilters from './Finance.ContractRefundPaymentItem.Filters';
import ContractRefundPaymentItemInfo from './Finance.ContractRefundPaymentItem.Info';

export default function ContractRefundPaymentItemList() {
  const { lang, translate } = getLocales();
  const { listApi, pageRouter, selectedItems, permissions, toggleItem, paymentCount, toggleSelectAll } =
    useContractRefundPaymentItemProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.contractsPaymentPending}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      sidebar={<ContractRefundPaymentItemInfo />}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.contractsPaymentPending} />
          <Box class={ThemeWrapperStack}>
            {isMobile && <ContractRefundPaymentItemFilters />}
            <ContractRefundPaymentItemActions key="actions" />
          </Box>
        </>
      }
      subHeader={isDesktop && <ContractRefundPaymentItemFilters />}
      render={
        <table key="list" className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>
                {listApi.payload?.count > 0 && (
                  <Button
                    class={ThemeButtonCircle}
                    media={
                      selectedItems?.length === paymentCount
                        ? 'fas-check-square'
                        : selectedItems?.length !== paymentCount
                          ? 'fas-minus-square'
                          : 'far-square'
                    }
                    data={!(selectedItems?.length === paymentCount)}
                    onClick={toggleSelectAll}
                    active={selectedItems?.length === paymentCount}
                    htmlElementProps={{ title: lang.selectAll }}
                  />
                )}
              </th>
              <th>{`${lang.refund} #`}</th>
              <th>{lang.appNumber}</th>
              <th>{lang.templateName}</th>
              <th>{lang.paymentMethod}</th>
              <th>{lang.payeeName}</th>
              <th>{lang.amount}</th>
              <th>{lang.gstHstPaid}</th>
              <th>{lang.pstPaid}</th>
              <th>{lang.total}</th>
              <th>{lang.createdAt}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: contractRefundPaymentItem }) => {
                const isSelected = selectedItems?.find((it) => it.id === contractRefundPaymentItem.id);

                return (
                  <React.Fragment key={contractRefundPaymentItem.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <Button
                          data={contractRefundPaymentItem}
                          onClick={toggleItem}
                          class={ThemeButtonCircle}
                          media={<Icon class={isSelected ? 'fas-check-square' : 'far-square'} />}
                          disabled={
                            !permissions.CREATE_CONTRACTREFUNDPAYMENT ||
                            contractRefundPaymentItem.isCancelled ||
                            contractRefundPaymentItem.isExcluded
                          }
                          htmlElementProps={{
                            title: !permissions.CREATE_CONTRACTREFUNDPAYMENT
                              ? lang.unauthorized
                              : contractRefundPaymentItem.isCancelled
                                ? lang.cancelled
                                : contractRefundPaymentItem.isExcluded
                                  ? lang.excluded
                                  : isSelected
                                ? lang.unselect
                                : lang.select,
                          }}
                        />
                      </td>
                      <td>
                        <b>{contractRefundPaymentItem.jobId}</b>
                      </td>
                      <td>{contractRefundPaymentItem.appNumber ?? 'N/A'}</td>
                      <td>{contractRefundPaymentItem.templateName ?? 'N/A'}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.XS }}>
                          <span>
                            {contractRefundPaymentItem.isCancelled && (
                              <Box
                                class={ThemeTagRounded}
                                style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                              >
                                <Icon class="fas-check" />
                                &nbsp; {lang.cancelled}
                              </Box>
                            )}
                            {contractRefundPaymentItem.isExcluded && (
                              <Box
                                class={ThemeTagRounded}
                                style={{ color: Theme.Color.warning, backgroundColor: Theme.Color.warningLight }}
                              >
                                <Icon class="fas-ban" />
                                &nbsp; {lang.excluded}
                              </Box>
                            )}
                          </span>
                          <span>{translate(contractRefundPaymentItem.paymentMethodCode)}</span>
                        </div>
                      </td>
                      <td>{contractRefundPaymentItem.contractRefundPayee?.displayInfo?.title ?? 'N/A'}</td>
                      <td>
                        <FormatValue value={`${(contractRefundPaymentItem.totalAmountBeforeTax || 0).toFixed(2)}$`} />
                      </td>
                      <td>
                        <FormatValue value={`${(contractRefundPaymentItem.gstHstAmount || 0).toFixed(2)}$`} />
                      </td>
                      <td>
                        <FormatValue
                          value={`${(contractRefundPaymentItem.provincialSalesTaxAmount || 0).toFixed(2)}$`}
                        />
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(contractRefundPaymentItem.totalAmount || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        {contractRefundPaymentItem.createdBy?.fullName && (
                          <>
                            {contractRefundPaymentItem.createdBy.fullName} {lang.at}
                            <br />
                          </>
                        )}
                        {contractRefundPaymentItem.createdAtLocal
                          ? localizeDate(contractRefundPaymentItem?.createdAtLocal, defaultDateTimeFormat)
                          : 'N/A'}
                      </td>
                      <td>
                        <Box class={ThemeWrapperStack}>
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-info-circle" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefundPaymentItem.id.toString(),
                              action: ContractRefundPaymentItemAction.INFO,
                            }}
                            htmlElementProps={{ title: lang.info }}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-ban" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefundPaymentItem.id.toString(),
                              action: ContractRefundPaymentItemAction.EXCLUDE,
                            }}
                            disabled={
                              !permissions.EDIT_CONTRACTREFUNDPAYMENTITEM ||
                              contractRefundPaymentItem.isCancelled ||
                              contractRefundPaymentItem.isExcluded
                            }
                            htmlElementProps={{
                              title: !permissions.EDIT_CONTRACTREFUNDPAYMENTITEM
                                ? lang.unauthorized
                                : contractRefundPaymentItem.isCancelled
                                  ? lang.cancelled
                                  : contractRefundPaymentItem.isExcluded
                                    ? lang.excluded
                                    : lang.exclude,
                            }}
                            active
                          />
                        </Box>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
